import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Grid,
	Card,
	CardContent,
	Typography,
	Button,
	Chip,
	Skeleton,
	alpha,
} from "@mui/material";
import {
	TrendingUp,
	CheckCircle,
	Error,
	PlayCircle,
	Science,
	BugReport,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import api from "../services/api";
import { timeAgo } from "../utils/dateUtils";

function formatDuration(ms) {
	if (!ms) return "—";
	const s = Math.floor(ms / 1000);
	const m = Math.floor(s / 60);
	if (m === 0) return `${s}s`;
	return `${m}m ${s % 60}s`;
}


const StatCard = ({ title, value, subtitle, icon: Icon, color, loading }) => (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.4 }}
	>
		<Card
			sx={{
				height: "100%",
				background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
				border: `1px solid ${alpha(color, 0.1)}`,
			}}
		>
			<CardContent>
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
					}}
				>
					<Box>
						<Typography
							variant="body2"
							color="text.secondary"
							gutterBottom
						>
							{title}
						</Typography>
						{loading ? (
							<Skeleton width={80} height={40} />
						) : (
							<Typography
								variant="h4"
								fontWeight={700}
								sx={{ mb: 1 }}
							>
								{value}
							</Typography>
						)}
						{subtitle && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<TrendingUp sx={{ fontSize: 16, color }} />
								<Typography variant="body2" sx={{ color }}>
									{subtitle}
								</Typography>
							</Box>
						)}
					</Box>
					<Box
						sx={{
							width: 56,
							height: 56,
							borderRadius: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							bgcolor: alpha(color, 0.1),
						}}
					>
						<Icon sx={{ fontSize: 28, color }} />
					</Box>
				</Box>
			</CardContent>
		</Card>
	</motion.div>
);

const RecentExecutionCard = ({ execution }) => (
	<motion.div
		initial={{ opacity: 0, x: -20 }}
		animate={{ opacity: 1, x: 0 }}
		transition={{ duration: 0.3 }}
	>
		<Card sx={{ mb: 2, "&:hover": { boxShadow: 4 } }}>
			<CardContent>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							variant="h6"
							fontWeight={600}
							gutterBottom
							noWrap
						>
							{execution.testName || "Unnamed Test"}
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								flexWrap: "wrap",
							}}
						>
							<Chip
								size="small"
								label={
									execution.status === "SUCCESS"
										? "Passed"
										: execution.status === "FAILED"
											? "Failed"
											: execution.status
								}
								color={
									execution.status === "SUCCESS"
										? "success"
										: execution.status === "FAILED"
											? "error"
											: "default"
								}
								icon={
									execution.status === "SUCCESS" ? (
										<CheckCircle />
									) : execution.status === "FAILED" ? (
										<Error />
									) : undefined
								}
							/>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								{formatDuration(execution.duration)}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								•
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								{timeAgo(execution.startedAt)}
							</Typography>
						</Box>
					</Box>
				</Box>
			</CardContent>
		</Card>
	</motion.div>
);

export default function Dashboard() {
	const navigate = useNavigate();
	const [overview, setOverview] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		api.get("/analytics/overview", { params: { workspaceId: "default" } })
			.then((res) => setOverview(res.data))
			.catch(() => {})
			.finally(() => setLoading(false));
	}, []);

	const recentExecutions = overview?.recentExecutions ?? [];

	return (
		<Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h4" fontWeight={700} gutterBottom>
					Dashboard
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Welcome back! Here's what's happening with your tests.
				</Typography>
			</Box>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						title="Total Tests"
						value={overview?.totalTests ?? "—"}
						icon={Science}
						color="#6366f1"
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						title="Test Runs"
						value={
							overview?.totalExecutions != null
								? overview.totalExecutions.toLocaleString()
								: "—"
						}
						icon={PlayCircle}
						color="#10b981"
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						title="Success Rate"
						value={
							overview?.successRate != null
								? `${overview.successRate}%`
								: "—"
						}
						subtitle={
							overview?.passedExecutions != null
								? `${overview.passedExecutions} passed`
								: undefined
						}
						icon={CheckCircle}
						color="#f59e0b"
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						title="Healing Events"
						value={overview?.totalHealingEvents ?? "—"}
						icon={BugReport}
						color="#ec4899"
						loading={loading}
					/>
				</Grid>
			</Grid>

			<Grid container spacing={3}>
				<Grid item xs={12} lg={8}>
					<Card>
						<CardContent>
							<Typography
								variant="h6"
								fontWeight={600}
								gutterBottom
							>
								Recent Test Executions
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 3 }}
							>
								Your latest test runs and their results
							</Typography>
							{loading ? (
								[1, 2, 3].map((i) => (
									<Skeleton
										key={i}
										variant="rectangular"
										height={80}
										sx={{ mb: 2, borderRadius: 1 }}
									/>
								))
							) : recentExecutions.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ py: 4, textAlign: "center" }}
								>
									No executions yet. Run a test to see results
									here.
								</Typography>
							) : (
								recentExecutions.map((execution) => (
									<RecentExecutionCard
										key={execution.id}
										execution={execution}
									/>
								))
							)}
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} lg={4}>
					<Card>
						<CardContent>
							<Typography
								variant="h6"
								fontWeight={600}
								gutterBottom
							>
								Quick Actions
							</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1.5,
									mt: 2,
								}}
							>
								<Button
									variant="contained"
									fullWidth
									startIcon={<Science />}
									sx={{ justifyContent: "flex-start" }}
									onClick={() => navigate("/tests/new")}
								>
									Create New Test
								</Button>
								<Button
									variant="outlined"
									fullWidth
									startIcon={<PlayCircle />}
									sx={{ justifyContent: "flex-start" }}
									onClick={() => navigate("/tests")}
								>
									View All Tests
								</Button>
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
}
